import { useRef, useState, type FormEvent } from 'react'
import { ScanBarcode, CheckCircle2, AlertTriangle, XCircle, RotateCcw, Camera, Upload } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { CameraScan } from '../components/CameraScan'
import { useActiveTenant } from '../contexts/tenant'

type Kind = 'ok' | 'dup' | 'blocked' | 'fail'
type Entry = { code: string; kind: Kind; text: string; at: string }
type ImportRow = { orderNumber: string; nfNumber?: string; customerName?: string }

const pick = (o: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const k of keys) {
    const v = o?.[k]
    if (v != null && v !== '') return String(v)
  }
  return undefined
}

/** Lê controle.json (objeto pedido→{nfe,...}) OU um array de pedidos, tolerante a nomes de campo. */
function parseReportOrders(raw: string): ImportRow[] {
  const j = JSON.parse(raw)
  const out: ImportRow[] = []
  const NF = ['nfe', 'nf', 'nfNumber', 'nf_number', 'nota', 'numero_nf']
  const CLI = ['cliente', 'customer', 'customerName', 'nome', 'destinatario']
  if (Array.isArray(j)) {
    for (const o of j) {
      if (!o || typeof o !== 'object') continue
      const num = pick(o, ['pedido', 'orderNumber', 'order_number', 'numero', 'id'])
      if (num) out.push({ orderNumber: num, nfNumber: pick(o, NF), customerName: pick(o, CLI) })
    }
  } else if (j && typeof j === 'object') {
    for (const [key, v] of Object.entries(j as Record<string, unknown>)) {
      if (v && typeof v === 'object') {
        const o = v as Record<string, unknown>
        out.push({ orderNumber: String(key), nfNumber: pick(o, NF), customerName: pick(o, CLI) })
      }
    }
  }
  return out
}

const styles: Record<Kind, { bar: string; Icon: typeof CheckCircle2; tone: 'ok' | 'warn' | 'err' }> = {
  ok:      { bar: 'border-green-300 bg-green-50 text-green-800', Icon: CheckCircle2, tone: 'ok' },
  dup:     { bar: 'border-amber-300 bg-amber-50 text-amber-800', Icon: RotateCcw, tone: 'warn' },
  blocked: { bar: 'border-red-300 bg-red-50 text-red-800', Icon: AlertTriangle, tone: 'warn' },
  fail:    { bar: 'border-red-300 bg-red-50 text-red-800', Icon: XCircle, tone: 'err' },
}

export function ExpedirScanPage() {
  const tenantId = useActiveTenant()
  const inputRef = useRef<HTMLInputElement>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [log, setLog] = useState<Entry[]>([])
  const [cameraOn, setCameraOn] = useState(false)
  const [camStatus, setCamStatus] = useState<{ text: string; tone: 'ok' | 'warn' | 'err' } | null>(null)
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState<{ text: string; ok: boolean } | null>(null)
  const busyRef = useRef(false)

  async function handleImport(file: File) {
    setImporting(true)
    setImportMsg(null)
    try {
      const parsed = parseReportOrders(await file.text())
      if (!parsed.length) { setImportMsg({ text: 'Nenhum pedido encontrado no arquivo.', ok: false }); return }
      const { data } = await api.post('/outbound/import-report', { tenantId, orders: parsed })
      setImportMsg({ text: `${parsed.length} lidos → ${data.created} criados, ${data.updated} c/ NF preenchida, ${data.skipped} já existiam. Já dá pra bipar.`, ok: true })
    } catch {
      setImportMsg({ text: 'Erro ao importar — confira se o arquivo é o JSON certo.', ok: false })
    } finally {
      setImporting(false)
    }
  }

  async function shipCode(raw: string) {
    const c = raw.trim()
    if (!c || busyRef.current) return
    busyRef.current = true
    setBusy(true)
    const at = new Date().toLocaleTimeString('pt-BR')
    try {
      const { data: r } = await api.post('/expedition/ship-scan', { tenantId, code: c })
      const who = r.order ? `${r.order.wsCode ?? ''} · ${r.order.customerName ?? ''}`.trim() : ''
      let entry: Entry
      if (!r.matched) entry = { code: c, kind: 'fail', text: `Não encontrado: ${c}`, at }
      else if (r.alreadyShipped) entry = { code: c, kind: 'dup', text: `${who} — já estava enviado`, at }
      else if (r.blocked) entry = { code: c, kind: 'blocked', text: `${who} — status "${r.blocked}" não permite`, at }
      else entry = { code: c, kind: 'ok', text: `${who} — ENVIADO ✓`, at }
      setLog(l => [entry, ...l].slice(0, 60))
      setCamStatus({ text: entry.text, tone: styles[entry.kind].tone })
    } catch {
      setLog(l => [{ code: c, kind: 'fail' as const, text: 'Erro ao expedir', at }, ...l].slice(0, 60))
      setCamStatus({ text: 'Erro ao expedir', tone: 'err' })
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const c = code.trim()
    if (!c) return
    await shipCode(c)
    setCode('')
    inputRef.current?.focus()
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  const last = log[0]
  const enviados = log.filter(l => l.kind === 'ok').length

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Expedir (scan)</h2>
        <p className="text-sm text-gray-500">Bipe (leitor USB) ou use a <b>câmera do celular</b> na chave da NF / rastreio / código WS para marcar como <b>enviado</b>.</p>
        <p className="text-xs text-gray-400">Ao expedir, o estoque é <b>baixado automaticamente</b> (uma vez — reexpedir o mesmo pedido não baixa de novo).</p>
      </div>

      <Card>
        <CardContent className="py-5">
          <form onSubmit={submit} className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              autoFocus
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-4 py-3 text-lg font-mono focus:border-blue-500 focus:outline-none"
              placeholder="Bipe ou digite aqui…"
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            <Button type="submit" size="lg" loading={busy}><ScanBarcode className="h-5 w-5" /> Expedir</Button>
            <Button type="button" size="lg" variant="secondary" onClick={() => { setCamStatus(null); setCameraOn(true) }}>
              <Camera className="h-5 w-5" /> Câmera
            </Button>
          </form>

          {last && (() => {
            const s = styles[last.kind]
            return (
              <div className={`mt-4 flex items-center gap-3 rounded-lg border px-4 py-4 ${s.bar}`}>
                <s.Icon className="h-8 w-8 shrink-0" />
                <div>
                  <p className="text-lg font-semibold">{last.text}</p>
                  <p className="text-xs opacity-70 font-mono">{last.code}</p>
                </div>
              </div>
            )
          })()}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-gray-600">Sessão: <b className="text-green-600">{enviados} enviado(s)</b> · {log.length} scan(s)</span>
        {log.length > 0 && <button className="px-1 py-2 text-xs text-gray-400 hover:text-gray-600" onClick={() => setLog([])}>limpar histórico</button>}
      </div>

      {log.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-gray-100">
              {log.map((l, i) => {
                const s = styles[l.kind]
                return (
                  <li key={i} className="flex min-w-0 items-center gap-3 px-4 py-2.5 text-sm">
                    <s.Icon className={`h-4 w-4 shrink-0 ${l.kind === 'ok' ? 'text-green-600' : l.kind === 'dup' ? 'text-amber-600' : 'text-red-600'}`} />
                    <span className="min-w-0 flex-1 truncate text-gray-700">{l.text}</span>
                    <span className="max-w-[38%] shrink-0 truncate font-mono text-xs text-gray-400">{l.code}</span>
                    <span className="shrink-0 text-xs text-gray-400">{l.at}</span>
                  </li>
                )
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      <details className="rounded-lg border border-gray-200 bg-gray-50/60">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
          Importar pedidos (Pele Bonita / relatório)
        </summary>
        <div className="space-y-3 border-t border-gray-200 px-4 py-4">
          <p className="text-sm text-gray-500">
            Suba o <b>controle.json</b> (ou a lista de pedidos) para cadastrar os pedidos e poder bipar a DANFE.
            Só cadastra os pedidos — <b>não</b> mexe no estoque (a baixa acontece ao expedir/bipar ou pela Baixa da separação).
          </p>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-blue-300 bg-white px-4 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-50">
            <Upload className="h-4 w-4" /> {importing ? 'Importando…' : 'Escolher arquivo JSON'}
            <input type="file" accept=".json,application/json" className="hidden" disabled={importing}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = '' }} />
          </label>
          {importMsg && (
            <p className={`text-sm font-medium ${importMsg.ok ? 'text-green-700' : 'text-red-600'}`}>{importMsg.text}</p>
          )}
        </div>
      </details>

      {cameraOn && (
        <CameraScan hint="Aponte para o código de barras da DANFE" status={camStatus}
          onScan={shipCode} onClose={() => setCameraOn(false)} />
      )}
    </div>
  )
}
